/**
 * Every GraphQL document «کشورها» sends, built against the contract in
 * `parsina.bff.admin/src/graphql/typeDefs/countries.js`. One file, so a
 * contract change is a diff against one place and a test can match a call by
 * its operation name.
 *
 * NULL is null on every profile field: a default "follows *", a capability is
 * none; `weekendDays: []` is a stored "no weekend".
 */

export const PROFILE_FIELDS = `
  country defaultLanguage calendar weekStart weekendDays defaultCurrency defaultTimezone
  nationalIdRule nationalDialCode addressModel mapView { lat lon zoom } printCalendar printLanguage
  erxProvider councilRegistry smsProvider messagingChannels paymentRails billServices
  payrollProfile insuranceCatalog updatedBy updatedAt unsetFields
`;

const DEPLOYMENT_FIELDS = 'deployment { market ceilings { capability iranianValues offHere } }';

export const COUNTRY_PROFILES_QUERY = `
  query CountryProfiles {
    countryProfiles { ${DEPLOYMENT_FIELDS} editorEnabled profiles { ${PROFILE_FIELDS} } }
  }
`;

export const COUNTRY_PROFILE_QUERY = `
  query CountryProfile($country: String!) {
    countryProfile(country: $country) {
      country found ${DEPLOYMENT_FIELDS}
      row { ${PROFILE_FIELDS} }
      merged { ${PROFILE_FIELDS} }
      fallback { ${PROFILE_FIELDS} }
    }
  }
`;

export const COUNTRY_CHANGES_QUERY = `
  query CountryProfileChanges($country: String, $limit: Int) {
    countryProfileChanges(country: $country, limit: $limit) {
      id country changedBy changedAt created before after changedFields note
    }
  }
`;

export const UPDATE_COUNTRY_PROFILE = `
  mutation UpdateCountryProfile(
    $country: String!, $changes: CountryProfileChangesInput!, $note: String!,
    $expectedUpdatedAt: String, $confirm: [String!]
  ) {
    updateCountryProfile(
      country: $country, changes: $changes, note: $note,
      expectedUpdatedAt: $expectedUpdatedAt, confirm: $confirm
    ) { ${PROFILE_FIELDS} }
  }
`;

/** A country's FIRST row, as a copy of `template` ('*' or a country with a row). */
export const CREATE_COUNTRY_PROFILE = `
  mutation CreateCountryProfile($country: String!, $template: String, $note: String!) {
    createCountryProfile(country: $country, template: $template, note: $note) { ${PROFILE_FIELDS} }
  }
`;

const INSURER_FIELDS = `
  id country nameFa nameEn names { locale name } kind aliases isActive isSelfPay
  registryCode verifyProvider sortOrder updatedBy updatedAt
`;

/**
 * `editorEnabled`: whether the insurer editor (accounting's InsurerAdminService)
 * answers this operator — false = it does not, null = the BFF could not tell.
 */
export const COUNTRY_INSURERS_QUERY = `
  query CountryInsurers($country: String, $includeInactive: Boolean) {
    countryInsurers(country: $country, includeInactive: $includeInactive) {
      configured country editorEnabled
      insurers { ${INSURER_FIELDS} }
    }
  }
`;

/**
 * The countries the insurer catalogue has rows for — what a profile's
 * `insuranceCatalog` may name (PM-0246). The insurers tab's read, narrowed:
 * without `editorEnabled`, so it costs no editor probe.
 */
export const INSURER_COUNTRIES_QUERY = `
  query InsurerCatalogCountries {
    countryInsurers(includeInactive: true) { configured insurers { country isSelfPay } }
  }
`;

/** A new insurer in one country's catalogue (PM-0246). */
export const CREATE_INSURER = `
  mutation CreateInsurer($input: InsurerInput!, $note: String!) {
    createInsurer(input: $input, note: $note) { ${INSURER_FIELDS} }
  }
`;

/**
 * An existing insurer: its id and country never change. `expectedUpdatedAt` is
 * the version the editor read, sent back as it came (null = no check).
 */
export const UPDATE_INSURER = `
  mutation UpdateInsurer($id: ID!, $input: InsurerInput!, $note: String!, $expectedUpdatedAt: String) {
    updateInsurer(id: $id, input: $input, note: $note, expectedUpdatedAt: $expectedUpdatedAt) { ${INSURER_FIELDS} }
  }
`;

/** Who changed which insurer, newest first; `insurerId` null = every insurer. */
export const INSURER_CHANGES_QUERY = `
  query InsurerChanges($insurerId: ID, $limit: Int) {
    insurerChanges(insurerId: $insurerId, limit: $limit) {
      insurerId changedBy changedAt created before after changedFields note
    }
  }
`;

const SETTINGS_FIELDS = `
  clinicId erxEnabled smsEnabled insuranceEnabled mohmeOrgId universityId calendar updatedBy updatedAt
`;

const CLINIC_VIEW_FIELDS = `
  clinicId country profileFound ${DEPLOYMENT_FIELDS}
  countryProfile { ${PROFILE_FIELDS} }
  effective { ${PROFILE_FIELDS} }
  settings { ${SETTINGS_FIELDS} }
  history { id actor at note before after changedFields }
`;

export const CLINIC_COUNTRY_QUERY = `
  query ClinicCountrySettings($clinicId: ID!) {
    clinicCountrySettings(clinicId: $clinicId) { ${CLINIC_VIEW_FIELDS} }
  }
`;

export const SET_CLINIC_COUNTRY_SETTINGS = `
  mutation SetClinicCountrySettings(
    $clinicId: ID!, $changes: ClinicCountrySettingsChangesInput!, $note: String!, $expectedUpdatedAt: String
  ) {
    setClinicCountrySettings(
      clinicId: $clinicId, changes: $changes, note: $note, expectedUpdatedAt: $expectedUpdatedAt
    ) {
      settings { ${SETTINGS_FIELDS} }
      view { ${CLINIC_VIEW_FIELDS} }
    }
  }
`;
